import { Injectable, inject } from '@angular/core';

import { filter, take } from 'rxjs';

import { environment } from '@env/environment';

import { AuthService } from '@personal-area/global/authorization/services';
import { I18nService } from '@personal-area/global/i18n';

import { WebsocketFallbackService } from './websocket-fallback.service';
import { WebsocketClient } from './websocket.client';
import { WebsocketConnection } from './websocket.connection';

export interface IWebSocket {
  onopen: ((event: any) => any) | null;
  onclose: ((event: any) => any) | null;
  onmessage: ((event: any) => any) | null;
  onerror: ((event: any) => any) | null;

  close(): any;
  send(data: string | ArrayBuffer | Blob): any;
}

export type WebSocketConnectionFactory = (
  url: string,
  protocols: string | string[],
  name: string,
  reset: boolean
) => IWebSocket;

@Injectable({
  providedIn: 'root',
})
export class WebsocketFactory {
  private readonly _host: string;

  private readonly _connectionRegistry: { [key: string]: IWebSocket };
  private readonly _activeClients: { [key: string]: WebsocketClient };

  private readonly _connectionFactory: WebSocketConnectionFactory;
  private readonly _i18nService = inject(I18nService);

  constructor(
    private readonly _authService: AuthService,
    private readonly _wsFallbackService: WebsocketFallbackService,
  ) {
    const l = window.location;
    this._host = (l.protocol === 'http:' ? 'ws://' : 'wss://') + l.hostname;

    if (environment.socketHost[l.host]) {
      this._host = environment.socketHost[l.host];
    } else if (environment.socketHost['default']) {
      this._host = environment.socketHost['default'];
    }

    this._connectionRegistry = {};
    this._activeClients = {};

    this._connectionFactory = (
      url: string,
      protocols: string | string[] = [],
      name: string,
      reset = false
    ): IWebSocket => {
      if (reset || !this._connectionRegistry[name]) {
        this._connectionRegistry[name] = new WebSocket(url, protocols);
      }
      return this._connectionRegistry[name];
    };
  }

  public create(path: string, name: string): WebsocketClient {
    const accountTypes = 'trader,partner,exchanger,tp';

    if (!this._activeClients[name]) {
      this._activeClients[name] = new WebsocketClient(
        new WebsocketConnection(
          this._host +
            path +
            `?language=${this._i18nService.languageA2}&accountTypes=${accountTypes}`,
          this._connectionFactory,
          name,
        ),
        this._authService,
        this._wsFallbackService
      );
      this._authService.onLogout
        .pipe(
          take(1),
          filter(() => !!this._connectionRegistry[name])
        )
        .subscribe(() => {
          this._connectionRegistry[name].close();
          delete this._connectionRegistry[name];
          delete this._activeClients[name];
        });
    }

    return this._activeClients[name];
  }
}
